"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
const contactDetails = [{
  icon: MapPin,
  title: "Adresse",
  lines: ["A-Z Umzüge", "Schillerstr. 14", "32257 Bünde"]
}, {
  icon: Phone,
  title: "Telefon",
  lines: ["Mobil: 0172 62 74 452", "Büro: 05223 65 82 896"]
}, {
  icon: Mail,
  title: "E-Mail",
  lines: ["web.az@umzuege-az.de"]
}, {
  icon: Clock,
  title: "Öffnungszeiten",
  lines: ["Montag – Samstag: 07:00 – 20:00"]
}, {
  icon: Mail,
  title: "Alternativ",
  lines: ["Alternativ: Kontaktformular"]
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-center">
        <h3 className="font-default text-2xl text-green-800 dark:text-green-300 mb-4">Vielen Dank!</h3>
        <p className="text-green-700 dark:text-green-400 mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mx-auto">
          Weitere Nachricht senden
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Name ist erforderlich"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-foreground">Name <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Input placeholder="Ihr Name" className="bg-[#F9F9F9] dark:bg-input/30 rounded-lg border-border" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "E-Mail ist erforderlich",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Ungültige E-Mail-Adresse"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-foreground">E-Mail <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Input type="email" placeholder="ihre@email.de" className="bg-[#F9F9F9] dark:bg-input/30 rounded-lg border-border" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="telefon" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-foreground">Telefon (optional)</FormLabel>
            <FormControl>
              <Input type="tel" placeholder="Ihre Telefonnummer" className="bg-[#F9F9F9] dark:bg-input/30 rounded-lg border-border" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="betreff" rules={{
      required: "Betreff ist erforderlich"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans text-foreground">Betreff <span className="text-destructive">*</span></FormLabel>
            <FormControl>
              <Input placeholder="Betreff eingeben..." className="bg-[#F9F9F9] dark:bg-input/30 rounded-lg border-border" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Nachricht ist erforderlich"
    }} render={({
      field
    }) => <FormItem>
            <FormControl>
              <Textarea placeholder="Ihre Nachricht..." className="min-h-[150px] bg-[#F9F9F9] dark:bg-input/30 rounded-lg border-border resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="privacy" rules={{
      required: "Sie müssen der Datenschutzerklärung zustimmen"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-1">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-normal text-muted-foreground font-sans">
                Ich stimme der Verarbeitung meiner Daten gemäß der{" "}
                <Link to="#" className="text-primary hover:underline">
                  Datenschutzerklärung
                </Link>{" "}
                zu. <span className="text-destructive">*</span>
              </FormLabel>
              <FormMessage />
            </div>
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg px-8 py-3 text-base font-medium mt-4">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Wird gesendet...
          </> : "Nachricht senden"}
      </Button>
    </Form>;
}
export default function ContactFormInfo() {
  return <section id="contact-form-info" className="bg-background py-12 md:py-16">
      <div className="page-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          
          {}
          <div>
            <p className="text-muted-foreground font-sans text-base mb-8">
              Füllen Sie das Formular aus und wir melden uns innerhalb von 24 Stunden bei Ihnen.
            </p>
            
            <FormProvider formId="contact-form-main" sectionName="ContactFormInfo" defaultValues={{
            name: "",
            email: "",
            telefon: "",
            betreff: "",
            message: "",
            privacy: false
          }} submitText="Nachricht senden" successMessage="Ihre Nachricht wurde erfolgreich gesendet. Wir werden uns in Kürze bei Ihnen melden.">
              <ContactFormInner />
            </FormProvider>
          </div>

          {}
          <div>
            <p className="text-muted-foreground font-sans text-base mb-8">
              Sie können uns auch direkt erreichen.
            </p>

            <div className="space-y-8">
              {contactDetails.map((detail, i) => <div key={i} data-index={i} className="flex items-start gap-4">
                  <div className="bg-muted/50 p-3 rounded-lg shrink-0">
                    <detail.icon className="size-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-default font-semibold text-foreground text-lg mb-1">
                      {detail.title}
                    </h3>
                    <div className="space-y-1">
                      {detail.lines.map((line, j) => <p key={j} data-index={j} className="text-muted-foreground font-sans text-base">
                          {line}
                        </p>)}
                    </div>
                  </div>
                </div>)}
            </div>

            {}
            
          </div>

        </div>
      </div>
    </section>;
}